import { useEffect, useImperativeHandle, useRef, type KeyboardEvent, type Ref } from 'react';
import { playBeep } from './beep';
import { cx } from './cx';
import { IconScan } from './icons';

export interface ScanInputProps {
  /** Called with the trimmed code when Enter (or the scanner's suffix key) arrives. */
  onScan: (code: string) => void;
  /** Keys that end a scan. Scanners usually send Enter; some send Tab. */
  suffixKeys?: readonly string[];
  autoFocus?: boolean;
  /** Take focus back when it falls to the page (not when the person picks another control). */
  keepFocus?: boolean;
  /** Beep after each scan. Pass `status` to tell success from failure. */
  beep?: boolean;
  status?: 'idle' | 'ok' | 'error';
  disabled?: boolean;
  label?: string;
  placeholder?: string;
  className?: string;
  /** Lets a screen put the cursor back, for example after a dialog. */
  ref?: Ref<ScanInputHandle>;
}

export interface ScanInputHandle {
  focus: () => void;
}

export function ScanInput({
  onScan,
  suffixKeys = ['Enter'],
  autoFocus = true,
  keepFocus = true,
  beep = false,
  status = 'idle',
  disabled = false,
  label = 'Scan or type a barcode',
  placeholder = 'Scan or type, then Enter',
  className,
  ref,
}: ScanInputProps) {
  const local = useRef<HTMLInputElement>(null);

  useImperativeHandle(ref, () => ({ focus: () => local.current?.focus() }), []);

  useEffect(() => {
    if (autoFocus && !disabled) local.current?.focus();
  }, [autoFocus, disabled]);

  const submit = (el: HTMLInputElement) => {
    const code = el.value.trim();
    el.value = '';
    if (code === '') return;
    if (beep) playBeep('ok');
    onScan(code);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!suffixKeys.includes(e.key)) return;
    e.preventDefault();
    submit(e.currentTarget);
  };

  const onBlur = () => {
    if (!keepFocus || disabled) return;
    // Wait a tick: if focus went to another control, leave it there.
    setTimeout(() => {
      const active = document.activeElement;
      if (local.current && (active === null || active === document.body)) local.current.focus();
    }, 0);
  };

  return (
    <div className={cx('eb-scan', className)} data-status={status}>
      <span className="eb-scan__icon">
        <IconScan size={26} />
      </span>
      <input
        ref={local}
        className="eb-scan__input"
        type="text"
        inputMode="text"
        autoComplete="off"
        autoCapitalize="characters"
        autoCorrect="off"
        spellCheck={false}
        enterKeyHint="go"
        aria-label={label}
        placeholder={placeholder}
        disabled={disabled}
        onKeyDown={onKeyDown}
        onBlur={onBlur}
      />
    </div>
  );
}
